import { Box, List, Typography, Paper, ListItemButton, Alert, Link, Stack } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { CopyCampaignFragment, AvailableTicketsQuery } from '~/generated/graphql'
import { ToggleViewButton } from '~/components/common/ToggleButton'
import { SimpleDialog } from '~/components/common/SimpleDialog'
import { pagesPath } from '~/lib/$path'
import NextLink from 'next/link'
import { getAvailableTicketAlert } from '~/lib/feature/getAvailableTicketAlert'
import { LightTooltip } from '~/components/common/LightTooltip'
import { colorPalette } from '~/constants/styles/colors'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const useCampaign = (campaigns: readonly CopyCampaignFragment[]) => {
  const [isMoreView, setIsMoreView] = useState(false)
  const handleClick = useCallback(() => {
    setIsMoreView((prev) => !prev)
    if (isMoreView) {
      window.scrollTo(0, 0)
    }
  }, [isMoreView])
  const displayedCampaigns = useMemo(() => {
    return isMoreView ? campaigns : campaigns.slice(0, 1)
  }, [campaigns, isMoreView])

  return { handleClick, displayedCampaigns, isMoreView }
}

const CampaignSelect: React.FC<{
  campaigns: readonly CopyCampaignFragment[]
  handleCampaign: (campaign: CopyCampaignFragment) => void
  availableTickets?: (AvailableTicketsQuery['viewer']['company'] & { __typename: 'Company' })['availableTickets']
}> = ({ campaigns, handleCampaign, availableTickets }) => {
  const { displayedCampaigns, handleClick, isMoreView } = useCampaign(campaigns)
  const [dialogOpenCampaignId, setDialogOpenCampaignId] = useState<string | null>(null)

  return (
    <Box>
      <List component="nav">
        {displayedCampaigns.map((campaign, i) => {
          const { availableTicketAlert, shouldShowNoTicketDialog } = availableTickets
            ? getAvailableTicketAlert(campaign.category, availableTickets)
            : { availableTicketAlert: null, shouldShowNoTicketDialog: false }

          const handleClickCampaign = (campaign: CopyCampaignFragment) => {
            if (shouldShowNoTicketDialog) {
              setDialogOpenCampaignId(campaign.id)
            } else {
              handleCampaign(campaign)
            }
          }
          return (
            <>
              <ListItemButton
                key={i}
                onClick={() => handleClickCampaign(campaign)}
                component={Paper}
                sx={{
                  mb: 1,
                  py: 2,
                  px: 3,
                }}
              >
                {campaign.name}
              </ListItemButton>

              <SimpleDialog
                open={dialogOpenCampaignId === campaign.id}
                onOk={() => {
                  setDialogOpenCampaignId(null)
                  handleCampaign(campaign)
                }}
                onCancel={() => {
                  setDialogOpenCampaignId(null)
                }}
                onClose={() => {
                  setDialogOpenCampaignId(null)
                }}
                title="注意"
                okButtonText="次に進む"
                closeButtonText="閉じる"
              >
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ lineHeight: 2 }}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      {availableTicketAlert}
                    </Box>
                    のチケットがありません。
                    <br />
                    チケットは
                    <Link
                      component={NextLink}
                      href={pagesPath.contract.$url().pathname}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      チケットの購入・契約ページ
                    </Link>
                    よりご購入いただけます。
                    <Box component="span" sx={{ display: 'block', mt: 2 }}>
                      このまま進むと、キャンペーンを審査に提出できません。
                      <br />
                      次のページに進んでもよろしいですか？
                    </Box>
                  </Typography>
                </Alert>
              </SimpleDialog>
            </>
          )
        })}
      </List>

      <ToggleViewButton length={campaigns.length} handleClick={handleClick} isMoreView={isMoreView} />
    </Box>
  )
}

const CustomFormControlLabel: React.FC<{
  value: string
  icon?: React.ReactNode
  tooltip?: React.ReactNode
}> = ({ value, icon, tooltip }) => (
  <Stack gap={0.5} direction="row" alignItems="center">
    <Typography
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontWeight: 'bold',
        fontSize: { md: 16, xs: 14 },
      }}
    >
      {icon}
      {value}
    </Typography>
    {tooltip && (
      <LightTooltip title={tooltip} placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 'inherit', color: colorPalette.other.backdrop }} />
      </LightTooltip>
    )}
  </Stack>
)

export { CampaignSelect, CustomFormControlLabel }
